/**
 * The words a live lesson card prints (redesign spec, batch 4): its title,
 * its time line, and the one status line it shows at a time. Pure, so it is
 * unit tested; every time is in the reader's zone.
 */
import { matchDueAt, opensLine, timeLabel, whenLine } from './liveCalendar.ts';

export type LessonModeKey = 'match_review' | 'guidance_call';

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** "Live Video Lesson · Match Review", or "Free Intro Call". */
export function liveLessonTitle(b: { offeringType: string; lessonMode?: LessonModeKey | null; membershipId?: string | null; paymentMethod?: string | null }): string {
  if (b.offeringType === 'free_intro_call') return 'Free Intro Call';
  // A lesson in Monthly Coaching: "Monthly Coaching · Live Video Lesson", its mode on its page and in the call only (61).
  // Every such row (Ryan, 29 Oct), a plan's lesson known by its payment too.
  if (b.membershipId || b.offeringType === 'live_membership' || b.paymentMethod === 'membership') return 'Monthly Coaching · Live Video Lesson';
  // A single Live Video Lesson keeps its mode everywhere (82).
  if (b.lessonMode === 'guidance_call') return 'Live Video Lesson · Guidance Call';
  if (b.lessonMode === 'match_review') return 'Live Video Lesson · Match Review';
  return 'Live Video Lesson';
}

/** A Monthly Coaching lesson's mode, for its page and the call (61); null for any other lesson, whose title has it. */
export function monthLessonMode(b: { offeringType: string; lessonMode?: LessonModeKey | null; membershipId?: string | null }): string | null {
  if (!(b.membershipId || b.offeringType === 'live_membership')) return null;
  return b.lessonMode === 'guidance_call' ? 'Guidance Call' : b.lessonMode === 'match_review' ? 'Match Review' : null;
}

/** "Lesson 2 of 4", a Monthly Coaching lesson's second line on Lessons (61). */
export function monthPlaceLine(at: { n: number; total: number } | null): string | null {
  return at ? `Lesson ${at.n} of ${at.total}` : null;
}

/** "Tue, Oct 20 at 1:00 PM · 45 min". */
export function liveTimeLine(startAt: string, minutes: number, tz: string): string {
  return `${whenLine(startAt, tz)} · ${minutes} min`;
}

export type CardStatus =
  | { kind: 'join' }
  | { kind: 'starts_in'; text: string }
  | { kind: 'upload'; text: string }
  | { kind: 'uploaded'; text: string }
  | { kind: 'later'; text: string }
  | { kind: 'finished'; text: string };

/**
 * One status at a time, in order: the room open (Join), within the hour
 * ("Starts in 42 min"), a Match Review's match ("Upload your match by Sun" or
 * "Match uploaded · Justin is reviewing it"), then how far off it is
 * ("In 3 days", "In 5 hours").
 */
export function cardStatus(p: {
  now: number;
  startAt: string;
  windowOpenAt?: string;
  finishedAt?: string;
  lessonMode?: LessonModeKey | null;
  hasMatch: boolean;
  matchDueAt?: string;
  coachFirstName: string;
  playerFirstName: string;
  isCoach: boolean;
  tz: string;
}): CardStatus {
  const start = new Date(p.startAt).getTime();
  const open = p.windowOpenAt ? new Date(p.windowOpenAt).getTime() : start - 10 * MINUTE;
  // Finished and waiting to settle: the vocabulary's line for after a lesson.
  if (p.finishedAt) return { kind: 'finished', text: 'Your recording will be ready in a few minutes' };
  if (p.now >= open) return { kind: 'join' };
  const until = start - p.now;
  // Decision 127: when the call opens, relative ("Opens at 1:20 AM", "Opens tomorrow at ...", the weekday, the date).
  const opens = opensLine(open, p.tz, p.now);
  if (until <= HOUR) return { kind: 'starts_in', text: opens };
  if (p.lessonMode === 'match_review') {
    if (p.hasMatch) {
      return {
        kind: 'uploaded',
        text: p.isCoach ? `${p.playerFirstName}'s match is uploaded` : `Match uploaded · ${p.coachFirstName} is reviewing it`,
      };
    }
    return {
      kind: 'upload',
      text: matchUploadLine({ for: p.isCoach ? 'coachRow' : 'row', coachFirst: p.coachFirstName, playerFirst: p.playerFirstName }),
    };
  }
  return { kind: 'later', text: opens };
}

/** A deadline rounded down to the half hour, so it is never later than the server's. */
export function halfHourBefore(iso: string): string {
  const t = new Date(iso).getTime();
  return new Date(Math.floor(t / (30 * MINUTE)) * 30 * MINUTE).toISOString();
}

/** The local day key, YYYY-MM-DD. */
const dayOf = (iso: string, tz: string) => new Date(iso).toLocaleDateString('en-CA', { timeZone: tz });

/**
 * "8:30 PM tonight", "11:00 AM today", "9:00 AM tomorrow", or
 * "Thu, Oct 22 at 1:00 PM": a reply-by time, read from now.
 */
export function relativeWhen(iso: string, nowIso: string, tz: string): string {
  const d = dayOf(iso, tz);
  const today = dayOf(nowIso, tz);
  const tomorrow = dayOf(new Date(new Date(nowIso).getTime() + DAY).toISOString(), tz);
  const time = timeLabel(iso, tz);
  if (d === today) {
    const hour = Number(new Date(iso).toLocaleString('en-US', { hour: 'numeric', hour12: false, timeZone: tz }));
    return `${time} ${hour >= 17 ? 'tonight' : 'today'}`;
  }
  if (d === tomorrow) return `${time} tomorrow`;
  return whenLine(iso, tz);
}

/** A time beside another: the time alone on the same day, else the full line. */
export function timeBeside(iso: string, otherIso: string, tz: string): string {
  return dayOf(iso, tz) === dayOf(otherIso, tz) ? timeLabel(iso, tz) : whenLine(iso, tz);
}

/**
 * The requested-time inset on a card (batch 4): from the other side, with
 * Accept and Keep; or yours, waiting.
 */
export function requestInset(p: {
  mine: boolean;
  otherFirstName: string;
  fromStartAt: string;
  toStartAt: string;
  expiresAt: string;
  now: string;
  tz: string;
  /**
   * The coach answering the player's request. Unanswered, it does not keep
   * the time: the lesson goes back to the player's month, or a single is
   * refunded (audit 57).
   */
  answeredByCoach?: boolean;
  membership?: boolean;
}): { line: string; second?: string; keepLabel?: string } {
  const to = timeBeside(p.toStartAt, p.fromStartAt, p.tz);
  if (p.mine) return { line: `You asked to move this to ${to}. Waiting for ${p.otherFirstName}.` };
  const from = timeBeside(p.fromStartAt, p.now, p.tz);
  return {
    line: `${p.otherFirstName} asked to move this to ${whenLine(p.toStartAt, p.tz)}.`,
    second: `Reply by ${relativeWhen(halfHourBefore(p.expiresAt), p.now, p.tz)} ` + (!p.answeredByCoach ? `or it stays at ${from}.`
      : p.membership ? `or the lesson goes back to ${p.otherFirstName}'s month.`
      : `or the lesson is cancelled and ${p.otherFirstName} is refunded.`),
    keepLabel: `Keep ${timeLabel(p.fromStartAt, p.tz)}`,
  };
}

/** The Adjust Time inset: "Justin moved the lesson to 4:10." with OK; or yours, waiting. */
export function adjustInset(p: {
  mine: boolean;
  otherFirstName: string;
  fromStartAt: string;
  toStartAt: string;
  tz: string;
}): { line: string; keepLabel?: string } {
  const to = timeBeside(p.toStartAt, p.fromStartAt, p.tz);
  if (p.mine) return { line: `You moved the lesson to ${to}. Waiting for ${p.otherFirstName}.` };
  return { line: `${p.otherFirstName} moved the lesson to ${to}.`, keepLabel: `Keep ${timeLabel(p.fromStartAt, p.tz)}` };
}

export const firstNameOf = (name: string | undefined | null, fallback: string) =>
  (name ?? '').trim().split(/\s+/)[0] || fallback;

/** Stand-ins used while a name is unknown: no initials are drawn from them. */
const PLACEHOLDER_NAMES = new Set(['your coach', 'your player', 'coach', 'player']);

/** "JS" for "Justin Seed": the Coaches list's rule, the first letter of the first two words. */
export function initialsOf(name: string): string {
  if (PLACEHOLDER_NAMES.has(name.trim().toLowerCase())) return '';
  return name.trim().split(/\s+/).map((n) => n[0] ?? '').join('').slice(0, 2).toUpperCase();
}

/**
 * The recording footnote under a booking's button: "Parents are welcome to
 * join." only for a player whose profile says they are under 18. An unknown
 * age gets the first sentence alone.
 */
export function recordingLine(age?: number | null): string {
  const minor = typeof age === 'number' && age < 18;
  return `Every lesson is recorded so you can rewatch it.${minor ? ' Parents are welcome to join.' : ''}`;
}

/** The upload pill's line: "Uploading · 45%", or the verb alone with no figure yet. */
export function uploadLabel(progress: number | null | undefined, verb = 'Uploading'): string {
  return progress == null ? verb : `${verb} · ${Math.max(0, Math.min(100, Math.round(progress)))}%`;
}

/**
 * A live lesson row's date, for its right column: "Today 7:00 PM",
 * "Tomorrow 7:00 PM", "Thu 7:00 PM" within the week, "Nov 3 7:00 PM" after.
 */
export function rowWhen(startAt: string, tz: string, nowMs: number): string {
  const day = (ms: number) => new Date(ms).toLocaleDateString('en-CA', { timeZone: tz });
  const at = Date.parse(startAt);
  const time = timeLabel(startAt, tz);
  if (day(at) === day(nowMs)) return `Today ${time}`;
  if (day(at) === day(nowMs + 86_400_000)) return `Tomorrow ${time}`;
  const label = new Date(at).toLocaleDateString('en-US', at - nowMs < 6 * 86_400_000
    ? { weekday: 'short', timeZone: tz } : { month: 'short', day: 'numeric', timeZone: tz });
  return `${label} ${time}`;
}

/** "Oct 27": a finished lesson's day, in the reader's zone. */
function shortDay(iso: string, tz: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: tz });
}

/**
 * The line under a finished lesson's header: "Finished · 45 min · Oct 27",
 * "Cancelled · Oct 27", or "Missed · Oct 27". `minutes` is how long it ran.
 */
export function finishedLine(b: { status: string; scheduledStartAt: string }, minutes: number, tz: string): string {
  const day = shortDay(b.scheduledStartAt, tz);
  if (b.status.startsWith('cancelled')) return `Cancelled · ${day}`;
  if (b.status.startsWith('no_show')) return `Missed · ${day}`;
  return `Finished · ${minutes} min · ${day}`;
}

/** The coach adds notes to a finished lesson for 2 hours after it finished. */
export const NOTES_WINDOW_MS = 2 * HOUR;

/**
 * Until when the coach can add notes: 2 hours after the lesson finished
 * (finishedAt, or completedAt on an older booking, else the scheduled end).
 */
export function notesOpenUntil(b: {
  finishedAt?: string | null; completedAt?: string | null; scheduledStartAt: string; durationMinutes?: number;
}): number {
  const end = b.finishedAt ?? b.completedAt
    ?? new Date(new Date(b.scheduledStartAt).getTime() + (b.durationMinutes ?? 45) * MINUTE).toISOString();
  return new Date(end).getTime() + NOTES_WINDOW_MS;
}

/** "Today at 7:00 PM", "Tomorrow at 7:00 PM", "Tue at 7:00 PM" inside a week, else "Nov 3 at 7:00 PM". */
export function dayAndTime(iso: string, tz: string, nowMs: number): string {
  const day = (ms: number) => new Date(ms).toLocaleDateString('en-CA', { timeZone: tz });
  const at = Date.parse(iso);
  const time = timeLabel(iso, tz);
  if (day(at) === day(nowMs)) return `Today at ${time}`;
  if (day(at) === day(nowMs + DAY)) return `Tomorrow at ${time}`;
  const label = new Date(at).toLocaleDateString('en-US', at - nowMs < 6 * DAY
    ? { weekday: 'short', timeZone: tz } : { month: 'short', day: 'numeric', timeZone: tz });
  return `${label} at ${time}`;
}

/** Beside Join: "Starts in 8 min", "Starting now", "Started 12 min ago". */
export function joinLine(startAt: string, nowMs: number): string {
  const until = Date.parse(startAt) - nowMs;
  if (until >= MINUTE) return `Starts in ${Math.ceil(until / MINUTE)} min`;
  if (until > -MINUTE) return 'Starting now';
  return `Started ${Math.floor(-until / MINUTE)} min ago`;
}

export type RowStatus =
  | { kind: 'join'; text: string }
  | { kind: 'upload'; text: string }
  | { kind: 'uploaded'; text: string }
  | { kind: 'countdown'; text: string };

/**
 * A player's live lesson row on Lessons: one status line, always the next
 * action. The room open: Join, with how long until it starts. Otherwise a
 * Match Review "Upload your match by Sun" (and the Upload capsule) while the
 * match is missing, else "Match uploaded"; a Guidance Call or a Free Intro
 * Call counts down ("In 3 days", "In 5 hours", "Starts in 42 min"). The day
 * and time are the row's right column (ListRow).
 */
export function playerRowStatus(p: {
  now: number; startAt: string; windowOpenAt?: string; isIntro: boolean;
  lessonMode?: LessonModeKey | null; hasMatch: boolean; matchDueAt?: string; tz: string;
  /** For the line once the deadline has passed: "so Justin can watch it first". */
  coachFirstName?: string;
}): RowStatus {
  const start = Date.parse(p.startAt);
  const open = p.windowOpenAt ? Date.parse(p.windowOpenAt) : start - 10 * MINUTE;
  if (p.now >= open) return { kind: 'join', text: joinLine(p.startAt, p.now) };
  if (p.lessonMode === 'match_review') {
    if (p.hasMatch) return { kind: 'uploaded', text: 'Match uploaded' };
    return { kind: 'upload', text: matchUploadLine({ for: 'row', coachFirst: p.coachFirstName ?? 'your coach' }) };
  }
  // Decision 127: when the call opens, relative ("Opens at 1:20 AM", "Opens tomorrow at ...", the weekday, the date).
  return { kind: 'countdown', text: opensLine(open, p.tz, p.now) };
}

/** A Match Review's upload deadline: the server's, else 24 hours before the start (the same rule). */
export function matchDueOf(b: { scheduledStartAt: string; matchUploadDueAt?: string | null }): string {
  return b.matchUploadDueAt ?? matchDueAt(b.scheduledStartAt);
}

/** Where an upload line is read: Booked, a Lessons row, the lesson page's card, the Lesson Type sheet, the coach's row or card. */
export type UploadLineFor = 'booked' | 'row' | 'card' | 'sheet' | 'coachRow' | 'coachCard';

/**
 * Every Match Review upload line in the app, from here. Decision 70 (Ryan,
 * 28 Oct): "Upload your match as soon as you can (we recommend at least a
 * day before) so Ryan has time to review it and prepare for the call."
 * (decision 109, which replaced 70's wording), with no date or deadline
 * anywhere; it replaced the due time of 24 hours before the start, and
 * with it the room walk's finding 1 (a past day printed). A Lessons row
 * says it short, "Upload your match"; the coach's row reads "Ana uploads the
 * match before the lesson", and the coach's page "Ana hasn't uploaded yet" (165). The server takes a match until the lesson
 * starts.
 */
export function matchUploadLine(p: { for: UploadLineFor; coachFirst: string; playerFirst?: string }): string {
  const player = p.playerFirst ?? 'Your player';
  const soon = `Upload your match as soon as you can (we recommend at least a day before) so ${p.coachFirst} has time to review it and prepare for the call.`;
  switch (p.for) {
    case 'booked': case 'sheet': case 'card': return soon;
    case 'row': return 'Upload your match';
    case 'coachRow': return `${player} uploads the match before the lesson`;
    // The coach's lesson page while the match is missing (165, changes.md 6).
    case 'coachCard': return `${player} hasn't uploaded yet`;
  }
}

/**
 * A Lessons row's right column, short as the native rows are: "6:30 AM" today, "Tomorrow" or
 * "Yesterday", the weekday within the week ("Thu"), else the date ("Oct 30").
 */
export function listTime(iso: string, tz: string, nowMs: number): string {
  const day = (ms: number) => new Date(ms).toLocaleDateString('en-CA', { timeZone: tz });
  const at = Date.parse(iso);
  if (day(at) === day(nowMs)) return timeLabel(iso, tz);
  if (day(at) === day(nowMs + DAY)) return 'Tomorrow';
  if (day(at) === day(nowMs - DAY)) return 'Yesterday';
  if (Math.abs(at - nowMs) < 6 * DAY) return new Date(at).toLocaleDateString('en-US', { weekday: 'short', timeZone: tz });
  return new Date(at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: tz });
}

/**
 * The pinned upload bar's words, from the uploads' own documents (one status for every screen):
 * "Preparing your match · 40%" while the file goes up, "Preparing your match" while the server
 * prepares it, "Preparing 2 videos · 40%" for several, "Upload failed. Tap to try again".
 */
export function uploadBarLabel(uploads: { status: string; uploadProgress?: number; kind?: string }[]): string {
  const live = uploads.filter((u) => u.status !== 'failed');
  if (!live.length) return 'Upload failed. Tap to try again';
  const noun = live.length > 1 ? `${live.length} videos`
    : live[0].kind === 'live_call_player_video' ? 'your match' : 'your video';
  const going = live.filter((u) => u.status === 'uploading');
  if (!going.length) return `Preparing ${noun}`;
  const pct = Math.round(live.reduce((n, u) => n + (u.status === 'uploading' ? (u.uploadProgress ?? 0) : 100), 0) / live.length);
  return `Preparing ${noun} · ${Math.max(0, Math.min(100, pct))}%`;
}

/** The attendance question (Ryan, 28 Oct): "Did your 5:00 AM lesson with Ana happen?". */
export function attendanceQuestion(startAt: string, tz: string, otherFirst: string): string {
  return `Did your ${timeLabel(startAt, tz)} lesson with ${otherFirst} happen?`;
}

/**
 * A completed lesson's one status line (167, booking.md 5b): "Completed · Wed, Nov 4 · 8 min", the length the
 * call ran when the recording says, else the lesson's own.
 */
export function completedLine(b: { scheduledStartAt: string; durationMinutes?: number; recordingDurationSeconds?: number | null }, tz: string): string {
  const day = new Date(b.scheduledStartAt).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: tz });
  const min = b.recordingDurationSeconds && b.recordingDurationSeconds > 0 ? Math.max(1, Math.round(b.recordingDurationSeconds / 60)) : (b.durationMinutes ?? 45);
  return `Completed · ${day} · ${min} min`;
}
